import React, { useEffect, useRef, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa';
import Blog1 from '../../assets/yogesh-singh-blog-coding-practices.jpg';
import Blog2 from '../../assets/yogesh-singh-blog-ui-ux-design.jpg';
import Blog3 from '../../assets/yogesh-singh-blog-interface-design.jpg';

const POSTS = [
  {
    id: 1,
    category: 'Development',
    date: 'Sep 11, 2023',
    title: 'The Main Thing for Coder',
    description:
      'Prioritize user needs and experience so the software you create delivers real value. Keep feedback close and iterate to improve.',
    image: Blog1,
    href: '#blog',
  },
  {
    id: 2,
    category: 'UI/UX Design',
    date: 'Sep 8, 2023',
    title: 'Follow Your Design Process',
    description:
      'Great UI starts with research. Understand audiences, define journeys, and validate decisions through continuous testing and iteration.',
    image: Blog2,
    href: '#blog',
  },
  {
    id: 3,
    category: 'Interface Design',
    date: 'Oct 1, 2023',
    title: 'Secret to Create the Better Interface',
    description:
      'Strive for intuitive design that guides users naturally. Build feedback loops and refine patterns so the interface stays simple and usable.',
    image: Blog3,
    href: '#blog',
  },
];

const Blog = () => {
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);
  
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  
  return (
    <section
      id="blog"
      ref={rootRef}
      className="relative overflow-hidden bg-gradient-to-b from-[#070a10] to-[#0b0f17] py-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-gradient-to-tr from-accent/16 via-emerald-500/10 to-transparent blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative">
        <div
          className={[
            'text-center mb-12 md:mb-16 transition-all duration-700',
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
          ].join(' ')}
        >
          <p className="text-xs tracking-[0.22em] uppercase text-gray-400">Blog</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white tracking-tight">
            Latest Articles
          </h2>
          <p className="mt-4 text-sm md:text-base text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Thoughts, tips, and insights on frontend development.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {POSTS.map((post, idx) => (
            <article
              key={post.id}
              className={[
                'group rounded-2xl overflow-hidden bg-white/5 border border-white/10 backdrop-blur shadow-soft',
                'transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-accent/40',
                'transition-all duration-700',
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
              ].join(' ')}
              style={{ transitionDelay: inView ? `${idx * 60}ms` : '0ms' }}
            >
              <a href={post.href} className="block aspect-video overflow-hidden">
                <img
                  src={post.image}
                  alt={`Yogesh Singh — ${post.title}`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </a>

              <div className="p-6 space-y-3">
                <p className="text-xs text-gray-400">
                  {post.category} <span aria-hidden="true">•</span> {post.date}
                </p>

                <h3 className="text-lg font-semibold text-white tracking-tight line-clamp-2">
                  {post.title}
                </h3>

                <p className="text-sm text-gray-400 leading-relaxed line-clamp-3">
                  {post.description}
                </p>

                <a
                  href={post.href}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-white transition-colors"
                >
                  Read Article
                  <FaArrowRight className="text-accent transition-transform duration-200 group-hover:translate-x-1" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Blog